<template>
  <modal
    elem-id="modal_staff_subscription_plan"
    :title="modalTitle"
    :show="isVisible"
    size="medium"
    :close-label="t('Fechar')"
    :save-label="t('Salvar')"
    save-test-id="staff-subscription-plan-submit"
    :save-fn="handleSave"
    :save-disabled-fn="() => formRef?.isSaving.value === true"
    @close="closeModal"
    @dismiss="closeModal"
  >
    <template v-if="isVisible">
      <staff-subscription-plan-form
        ref="formRef"
        :plan="currentPlan"
        @saved="handleSaved"
        @validation-failed="() => {}"
      />

      <!-- Prices panel: shown only once the plan exists (edit mode) -->
      <template v-if="currentPlanId != null">
        <div class="plan-markets-section">
          <h3 class="plan-markets-section-title" data-testid="plan-markets-section-title">
            {{ t('Preços / Mercados') }}
          </h3>
          <plan-markets-panel
            :plan-id="currentPlanId"
            data-testid="plan-markets-panel-embedded"
            @changed="handleMarketsChanged"
          />
        </div>
      </template>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import Modal from '../../../ui/Modal.vue'
import StaffSubscriptionPlanForm from './StaffSubscriptionPlanForm.vue'
import PlanMarketsPanel from './PlanMarketsPanel.vue'
import { useSubscriptionAdmin } from '../../../composables/useSubscriptionAdmin'
import type { PlanGroup } from '../../../composables/useSubscriptionAdmin'

interface PlanFormExposed {
  handleSubmit: () => Promise<void>
  isSaving: { value: boolean }
  resetForm: () => void
  populateFromPlan: (plan: PlanGroup) => void
}

interface ModalOpenPayload {
  plan?: PlanGroup | null
  /**
   * D3: breadth of the list the opener is showing ("Mostrar inativos").
   * It travels in the payload because the toggle lives on StaffSubscriptionPage
   * while this modal is registered globally in ModalManager and has no way to
   * read it. Absent ⇒ active-only, the page's default.
   */
  includeInactive?: boolean
}

const { t } = useI18n()
const { listPlans, groupedPlans } = useSubscriptionAdmin()

const isVisible = ref(false)
const currentPlanId = ref<number | null>(null)
/** Breadth received from the opener; drives the re-list after a save. */
const listIncludesInactive = ref(false)
const formRef = ref<PlanFormExposed | null>(null)

/** Always derived from groupedPlans so it stays fresh after listPlans(). */
const currentPlan = computed<PlanGroup | null>(() => {
  if (currentPlanId.value == null) return null
  return groupedPlans.value.find((g) => g.id === currentPlanId.value) ?? null
})

const modalTitle = computed(() =>
  currentPlanId.value != null ? t('Editar plano') : t('Criar plano'),
)

function openModal(payload?: ModalOpenPayload): void {
  log('StaffSubscriptionPlanModal', 'openModal', 'Abrindo modal', payload)
  currentPlanId.value = payload?.plan?.id ?? null
  listIncludesInactive.value = payload?.includeInactive === true
  isVisible.value = true
}

function closeModal(): void {
  log('StaffSubscriptionPlanModal', 'closeModal', 'Fechando modal')
  isVisible.value = false
  currentPlanId.value = null
}

async function handleSave(): Promise<void> {
  if (formRef.value) {
    await formRef.value.handleSubmit()
  }
}

async function handleSaved(payload: { id: number; created: boolean }): Promise<void> {
  currentPlanId.value = payload.id
  // ONE re-list per save, and it is this one. The table renders off the SAME
  // `groupedPlans` singleton, so re-listing here already puts the saved plan on
  // its screen — emitting 'staffSubscription.refresh' as well only made the
  // table fetch the identical list a second time (1 mount + 2 re-lists = 3 GETs
  // for a single create).
  //
  // The breadth has to be carried across with it, and it ARRIVES here — the
  // opener puts its "Mostrar inativos" state in the open payload, because the
  // toggle lives on StaffSubscriptionPage and this modal is registered globally
  // in ModalManager. Do NOT infer it from the list on screen: with the toggle ON
  // and nothing retired yet, `groupedPlans.some(g => g.active === 0)` reads
  // false, the single re-list drops include_inactive, and the plan the staff
  // just retired vanishes from the very screen that asked to see retired plans.
  await listPlans({ includeInactive: listIncludesInactive.value })
  // Modal stays open — switches to edit mode and reveals the prices panel.
  log('StaffSubscriptionPlanModal', 'handleSaved', 'Plano salvo, mantendo modal aberto', payload)
}

async function handleMarketsChanged(): Promise<void> {
  eventBus.emit('staffSubscription.refresh')
}

onMounted(() => {
  eventBus.on('modal.staffSubscriptionPlan.open', openModal)
  eventBus.on('modal.staffSubscriptionPlan.close', closeModal)
})

onBeforeUnmount(() => {
  eventBus.off('modal.staffSubscriptionPlan.open', openModal)
  eventBus.off('modal.staffSubscriptionPlan.close', closeModal)
})
</script>

<style scoped>
.plan-markets-section {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--surface-border, #e0e0e0);
}

.plan-markets-section-title {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 12px 0;
}
</style>
